// src/components/saved-lists-components/Table.js
import React, { useMemo, useState, useEffect } from 'react';
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  usePagination
} from 'react-table';
import {
  TiArrowUnsorted,
  TiArrowSortedUp,
  TiArrowSortedDown
} from 'react-icons/ti';
import { GlobalFilter } from './GlobalFilter';

// helper to mask everything but the first N chars
const maskValue = (raw, visibleChars = 3) => {
  const s = String(raw);
  if (s === 'n/a' || s.length <= visibleChars) return s;
  return s.slice(0, visibleChars) + '•'.repeat(s.length - visibleChars);
};

export default function Table({
  data,
  updateCheckedState,
  updateNotes,
  updateTags,
  listType = 'business',
  accountType = 'Trial'    // ← new!
}) {
  const [rows, setRows] = useState(() => data);

  // keep our local copy in sync when data changes
  useEffect(() => {
    setRows(data);
  }, [data]);

  // custom sort for tags column
  const tagSort = (a, b) =>
    (a.original.tags?.length ?? 0) - (b.original.tags?.length ?? 0);

  const desiredOrder = {
    business: ['name', 'website', 'phone', 'address', 'rating', 'seoAudit'],
    people:   ['name', 'jobTitle', 'phone', 'location', 'email', 'university', 'profileLink']
  };

  const columns = useMemo(() => {
    // ── Fixed columns ───────────────────────────────────────────────
    const idCol = { Header: 'ID', accessor: 'id' };

    const checkedCol = {
      Header: '✓',
      accessor: 'checked',
      Cell: ({ row }) => {
        const rowId = row.original.id;
        const toggle = () => {
          setRows(r =>
            r.map(v => v.id === rowId ? { ...v, checked: !v.checked } : v)
          );
          updateCheckedState(rowId, !row.original.checked);
        };
        return <input type="checkbox" checked={row.original.checked} onChange={toggle} />;
      },
      sortType: (a, b) =>
        a.original.checked === b.original.checked
          ? 0
          : a.original.checked ? 1 : -1
    };

    const notesCol = {
      Header: 'Notes',
      accessor: 'notes',
      Cell: ({ row }) => {
        const rowId = row.original.id;
        const [draft, setDraft] = useState(row.original.notes ?? '');

        useEffect(() => {
          setDraft(row.original.notes ?? '');
        }, [row.original.notes]);

        const save = () => {
          if (draft !== row.original.notes) {
            setRows(r => r.map(v => v.id === rowId ? { ...v, notes: draft } : v));
            updateNotes(rowId, draft);
          }
        };

        return (
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={save}
          />
        );
      }
    };

    const tagsCol = {
      Header: 'Tags',
      accessor: 'tags',
      Cell: ({ row }) => {
        const rowId = row.original.id;

        const addTag = e => {
          if (e.key !== 'Enter' || !e.target.value.trim()) return;
          const tag = e.target.value.trim();
          const upd = rows.map(r =>
            r.id === rowId ? { ...r, tags: [...(r.tags||[]), tag] } : r
          );
          setRows(upd);
          updateTags(rowId, upd.find(r => r.id === rowId).tags);
          e.target.value = '';
        };
        const removeTag = tag => {
          const upd = rows.map(r =>
            r.id === rowId
              ? { ...r, tags: r.tags.filter(t => t !== tag) }
              : r
          );
          setRows(upd);
          updateTags(rowId, upd.find(r => r.id === rowId).tags);
        };

        return (
          <div className="tags-cell">
            {(row.original.tags||[]).map((t,i) => (
              <span key={i} className="tag">
                {t} <button onClick={() => removeTag(t)}>×</button>
              </span>
            ))}
            <input placeholder="add" onKeyDown={addTag} />
          </div>
        );
      },
      sortType: tagSort
    };

    // ── Dynamic columns ─────────────────────────────────────────────
    const baseKeys = ['id','checked','notes','tags'];
    const allKeys = Object.keys(rows[0]||{}).filter(k => !baseKeys.includes(k));
    const orderedKeys = [
      ...(desiredOrder[listType]||[]),
      ...allKeys.filter(k => !(desiredOrder[listType]||[]).includes(k)).sort()
    ];
    const dynamicCols = orderedKeys.map(key => ({
      Header: key,
      accessor: key,
      Cell: ({ value }) => {
        // mask for Trial users (except the name column)
        if (accountType === 'Trial' && key !== 'name') {
          return maskValue(value);
        }
        if (/^(https?:\/\/)/i.test(value)) {
          return <a href={value} target="_blank" rel="noreferrer">{value}</a>;
        }
        return value;
      }
    }));

    return [ checkedCol, ...dynamicCols, notesCol, tagsCol ];
  }, [
    rows,
    listType,
    updateCheckedState,
    updateNotes,
    updateTags,
    accountType      // ← include in deps so we recompute when it changes
  ]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    setPageSize,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter }
  } = useTable(
    { columns, data: rows, initialState: { pageSize: 20 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  return (
    <>
      {rows.length > 1 && (
        <GlobalFilter
          preGlobalFilteredRows={preGlobalFilteredRows}
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
        />
      )}

      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(hg => (
            <tr {...hg.getHeaderGroupProps()}>
              {hg.headers.map(col => (
                <th {...col.getHeaderProps(col.getSortByToggleProps())}>
                  {col.render('Header')}
                  {col.isSorted
                    ? (col.isSortedDesc ? <TiArrowSortedDown /> : <TiArrowSortedUp />)
                    : <TiArrowUnsorted />}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map(row => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => (
                  <td {...cell.getCellProps()}>
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {rows.length > 1 && (
        <div className="pagination-controlls-container">
          <p className="page-count-text">
            Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong>
          </p>
          <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
            {[20, 50, 100].map(s => (
              <option key={s} value={s}>show {s}</option>
            ))}
          </select>
          <button onClick={previousPage} disabled={!canPreviousPage}>prev</button>
          <button onClick={nextPage}     disabled={!canNextPage}>next</button>
        </div>
      )}
    </>
  );
}
